<script setup lang="ts">
import { computed, onMounted, ref, useTemplateRef, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Layout from '@/components/design/layout/Layout.vue';
import GaiaChat from '@/components/design/domain/chat/GaiaChat.vue';
import { useAuthStore } from '@/stores/auth';

// The chat as a destination of its own: the same GaiaChat the home screen
// mounts, without home's greeting and cards around it. The open conversation
// lives in the query on the same `?thread_uid=` contract as home, so links to
// a conversation work from either page and back/forward walk conversations
// here the same way.

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

/** Initials on the user's own chat bubbles — "Janina Möllmann" → "JM". */
const userInitials = computed(() =>
  (authStore.backendUser?.full_name ?? '')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part.charAt(0).toUpperCase())
    .join('')
);

const askValue = ref('');

const chatRef = useTemplateRef<{
  openThread: (uid: string) => Promise<void>;
  newChat: () => void;
}>('chat');

/** The conversation GaiaChat currently has on screen. */
const openThreadUid = ref<string | null>(null);

function queryString(value: unknown): string | null {
  if (typeof value === 'string' && value.trim().length > 0) return value;
  if (Array.isArray(value)) {
    const first = value.find(item => typeof item === 'string' && item.trim().length > 0);
    return typeof first === 'string' ? first : null;
  }
  return null;
}

const routeThreadUid = computed(() => queryString(route.query.thread_uid));

/** URL → view. Also what back and forward run through. */
function applyRouteToChat(): void {
  const uid = routeThreadUid.value;
  // Claim the uid before the load: `openThread` awaits a rejoined run to the
  // end, so nothing downstream may depend on that promise having settled.
  if (uid && uid !== openThreadUid.value) {
    openThreadUid.value = uid;
    void chatRef.value?.openThread(uid);
  } else if (!uid && openThreadUid.value) {
    openThreadUid.value = null;
    chatRef.value?.newChat();
  }
}

watch(routeThreadUid, applyRouteToChat);

// View → URL, guarded by comparing against the URL rather than a "currently
// applying" flag: the component echoes back every change we apply from a
// back/forward, and an echo that matches the query is nothing to write.
function onThreadChange(uid: string | null): void {
  const isNewThread = routeThreadUid.value === null;
  openThreadUid.value = uid;
  if (uid === routeThreadUid.value) return;
  // A thread that just learned its uid is this step gaining an identity, so it
  // replaces; anything else is a move to another conversation, so it pushes.
  void (uid !== null && isNewThread
    ? router.replace({ query: { thread_uid: uid } })
    : router.push({ query: uid ? { thread_uid: uid } : {} }));
}

onMounted(() => {
  // A reload or a pasted link lands here with the conversation already in the
  // query. The watcher above only fires on changes, so the first read is here —
  // after mount, when the GaiaChat ref exists to be driven.
  applyRouteToChat();
});
</script>

<template>
  <Layout>
    <!-- `chatting` is pinned: this page has no hero state to collapse back to,
         so the chat always shows its expanded view (thread header + transcript
         + compact composer), even before the first message. The component only
         ever emits `update:chatting` true, so ignoring the echo loses nothing. -->
    <GaiaChat
      ref="chat"
      v-model:value="askValue"
      chatting
      attachable
      follow-up-placeholder="Ask anything about your contracts…"
      drop-hint="Drop files to attach them to your question"
      :user-initials="userInitials"
      @thread-change="onThreadChange"
      @open-reference="path => router.push(path)"
    />
  </Layout>
</template>
